import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import { getArgumentLabel, getConfirmationRenderer } from '../confirmationDisplay';
import { CHAT_EVENTS, CONFIRMATION_NOTE_MAX_LENGTH, CONFIRMATION_STATUS, trackChatEvent } from '../constants';
import css from './ConfirmationCard.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.CONFIRMATION';

// Arguments shown before the list collapses. Six covers most calls outright.
const COLLAPSED_ARGUMENT_COUNT = 6;

// A one-hour expiry spends most of its life irrelevant, so the clock stays hidden until it
// is close enough to change a decision, and ticks from then on.
const EXPIRY_WARNING_MINUTES = 10;
const EXPIRY_TICK_MS = 30 * 1000;

// The sentence keeps the tool name in one translatable string while still rendering it as an
// element rather than as HTML, which the server-supplied label must never become.
const TOOL_PLACEHOLDER = '${tool}';

const minutesUntil = expiresAt => {
    if (expiresAt == null) return null;
    return Math.ceil((expiresAt * 1000 - Date.now()) / 60000);
};

const useMinutesLeft = expiresAt => {
    const [minutesLeft, setMinutesLeft] = useState(() => minutesUntil(expiresAt));

    useEffect(() => {
        setMinutesLeft(minutesUntil(expiresAt));
        if (expiresAt == null) return undefined;
        const timer = setInterval(() => setMinutesLeft(minutesUntil(expiresAt)), EXPIRY_TICK_MS);
        return () => clearInterval(timer);
    }, [expiresAt]);

    return minutesLeft;
};

const ArgumentValue = ({ value, emptyLabel }) => {
    if (value === null || value === undefined || value === '') return <span className={css.unset}>{emptyLabel}</span>;
    if (typeof value === 'boolean') return <span>{String(value)}</span>;
    if (typeof value === 'object') return <pre className={css.json}>{JSON.stringify(value, null, 2)}</pre>;
    return <span>{String(value)}</span>;
};

ArgumentValue.propTypes = {
    value: PropTypes.any,
    emptyLabel: PropTypes.string.isRequired,
};

const GenericArguments = ({ toolName, input, translate }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const entries = useMemo(() => Object.entries(input || {}), [input]);

    if (!entries.length) return <div className={css.unset}>{translate(`${TRANSLATION_PREFIX}.NO_ARGUMENTS`)}</div>;

    const hiddenCount = entries.length - COLLAPSED_ARGUMENT_COUNT;
    const isCollapsible = hiddenCount > 0;
    const shown = isCollapsible && !isExpanded ? entries.slice(0, COLLAPSED_ARGUMENT_COUNT) : entries;

    return (
        <React.Fragment>
            <div className={isExpanded ? `${css.arguments} ${css.argumentsScrollable}` : css.arguments}>
                {shown.map(([key, value]) => {
                    const label = getArgumentLabel(toolName, key);
                    return (
                        <React.Fragment key={key}>
                            <dt className={css.argumentName}>
                                {label || key}
                                {label && <span className={css.argumentKey}>{key}</span>}
                            </dt>
                            <dd className={css.argumentValue}>
                                <ArgumentValue
                                    value={value}
                                    emptyLabel={translate(`${TRANSLATION_PREFIX}.EMPTY_VALUE`)}
                                />
                            </dd>
                        </React.Fragment>
                    );
                })}
            </div>
            {isCollapsible && (
                <button type="button" className={css.expand} onClick={() => setIsExpanded(expanded => !expanded)}>
                    <Icon
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={IconSizes.X_SMALL}
                        variant={IconVariants.ACTIVE}
                        interactive={false}
                    />
                    {isExpanded
                        ? translate(`${TRANSLATION_PREFIX}.SHOW_FEWER_ARGUMENTS`)
                        : translate(`${TRANSLATION_PREFIX}.SHOW_MORE_ARGUMENTS`, { count: hiddenCount })}
                </button>
            )}
        </React.Fragment>
    );
};

GenericArguments.propTypes = {
    toolName: PropTypes.string,
    input: PropTypes.object,
    translate: PropTypes.func.isRequired,
};

const ConfirmationCard = ({ confirmation, onResolve, onDiscard, translate }) => {
    const { toolName, toolLabel, input, expiresAt, status, error } = confirmation;

    const isSubmitting = status === CONFIRMATION_STATUS.SUBMITTING;
    const minutesLeft = useMinutesLeft(expiresAt);
    const hasExpired = minutesLeft != null && minutesLeft <= 0;
    const isDead = status === CONFIRMATION_STATUS.TERMINAL || hasExpired;
    const showsClock = !isDead && minutesLeft != null && minutesLeft <= EXPIRY_WARNING_MINUTES;

    const Arguments = getConfirmationRenderer(toolName) || GenericArguments;

    const [note, setNote] = useState('');
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const noteRef = useRef(null);

    useEffect(() => {
        if (isNoteOpen) noteRef.current?.focus();
    }, [isNoteOpen]);

    const resolve = useCallback(
        (confirmed, withNote) => {
            trackChatEvent(confirmed ? CHAT_EVENTS.APPROVE_TOOL_CALL : CHAT_EVENTS.DECLINE_TOOL_CALL, {
                toolName,
                withNote: Boolean(withNote),
            });
            onResolve(confirmed, withNote);
        },
        [onResolve, toolName]
    );

    // A rejection carrying a note is answered by the agent inside the same run, so asking for
    // different arguments costs a sentence rather than a new turn.
    const sendNote = useCallback(() => {
        const trimmed = note.trim();
        if (!trimmed) return;
        resolve(false, trimmed);
    }, [note, resolve]);

    const onNoteKeyDown = useCallback(
        event => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) sendNote();
        },
        [sendNote]
    );

    const discard = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.DISCARD_TOOL_CALL, { toolName });
        onDiscard();
    }, [onDiscard, toolName]);

    const [sentenceStart, sentenceEnd] = translate(`${TRANSLATION_PREFIX}.RUN_WITH_ARGUMENTS`, {
        tool: TOOL_PLACEHOLDER,
    }).split(TOOL_PLACEHOLDER);

    return (
        <section
            className={isDead ? `${css.card} ${css.cardDead}` : css.card}
            aria-label={translate(`${TRANSLATION_PREFIX}.TITLE`)}
        >
            <header className={css.header}>
                <span className={isDead ? `${css.badge} ${css.badgeDead}` : css.badge}>
                    <Icon
                        name={isDead ? 'clock' : 'alert-triangle'}
                        type={IconTypes.TABLER}
                        size={IconSizes.SMALL}
                        variant={isDead ? IconVariants.LIGHT : IconVariants.WARNING}
                        interactive={false}
                    />
                </span>
                <h3 className={css.title}>
                    {isDead ? translate(`${TRANSLATION_PREFIX}.EXPIRED_TITLE`) : translate(`${TRANSLATION_PREFIX}.TITLE`)}
                </h3>
                {showsClock && (
                    <span className={css.expiry}>
                        <Icon
                            name="clock"
                            type={IconTypes.TABLER}
                            size={IconSizes.X_SMALL}
                            variant={IconVariants.WARNING}
                            interactive={false}
                        />
                        {translate(`${TRANSLATION_PREFIX}.EXPIRES_IN`, { minutes: minutesLeft })}
                    </span>
                )}
            </header>

            {isDead ? (
                <p className={css.lede}>
                    {translate(`${TRANSLATION_PREFIX}.EXPIRED_BODY`, { tool: toolLabel })}
                </p>
            ) : (
                <React.Fragment>
                    <p className={css.lede}>
                        {sentenceStart}
                        <strong className={css.toolLabel}>{toolLabel}</strong>
                        {sentenceEnd}
                    </p>
                    <div className={css.body}>
                        <Arguments toolName={toolName} input={input} translate={translate} />
                    </div>
                </React.Fragment>
            )}

            {error && <div className={css.error}>{error}</div>}

            {isNoteOpen && !isDead && (
                <textarea
                    ref={noteRef}
                    className={css.note}
                    value={note}
                    onChange={event => setNote(event.target.value)}
                    onKeyDown={onNoteKeyDown}
                    maxLength={CONFIRMATION_NOTE_MAX_LENGTH}
                    rows={2}
                    placeholder={translate(`${TRANSLATION_PREFIX}.NOTE_PLACEHOLDER`)}
                    aria-label={translate(`${TRANSLATION_PREFIX}.NOTE_LABEL`)}
                />
            )}

            <footer className={css.footer}>
                {/* Continues the turn rather than answering it, so it sits apart from the two
                    buttons that end it. */}
                {!isDead && !isNoteOpen ? (
                    <button
                        type="button"
                        className={css.secondary}
                        onClick={() => setIsNoteOpen(true)}
                        disabled={isSubmitting}
                    >
                        {translate(`${TRANSLATION_PREFIX}.REQUEST_CHANGES`)}
                    </button>
                ) : (
                    <span />
                )}
                <div className={css.actions}>
                    {isDead && (
                        <button type="button" className={css.secondary} onClick={discard}>
                            {translate(`${TRANSLATION_PREFIX}.DISMISS`)}
                        </button>
                    )}
                    {!isDead && isNoteOpen && (
                        <React.Fragment>
                            <button
                                type="button"
                                className={css.link}
                                onClick={() => setIsNoteOpen(false)}
                                disabled={isSubmitting}
                            >
                                {translate(`${TRANSLATION_PREFIX}.CANCEL`)}
                            </button>
                            <button
                                type="button"
                                className={css.primary}
                                onClick={sendNote}
                                disabled={isSubmitting || !note.trim()}
                            >
                                {translate(`${TRANSLATION_PREFIX}.${isSubmitting ? 'SENDING' : 'SEND_CHANGES'}`)}
                            </button>
                        </React.Fragment>
                    )}
                    {!isDead && !isNoteOpen && (
                        <React.Fragment>
                            <button
                                type="button"
                                className={css.link}
                                onClick={() => resolve(false)}
                                disabled={isSubmitting}
                            >
                                {translate(`${TRANSLATION_PREFIX}.DECLINE`)}
                            </button>
                            <button
                                type="button"
                                className={css.primary}
                                onClick={() => resolve(true)}
                                disabled={isSubmitting}
                            >
                                {translate(`${TRANSLATION_PREFIX}.${isSubmitting ? 'SENDING' : 'APPROVE'}`)}
                            </button>
                        </React.Fragment>
                    )}
                </div>
            </footer>
        </section>
    );
};

ConfirmationCard.propTypes = {
    confirmation: PropTypes.shape({
        toolName: PropTypes.string,
        toolLabel: PropTypes.string,
        input: PropTypes.object,
        expiresAt: PropTypes.number,
        status: PropTypes.string.isRequired,
        error: PropTypes.string,
    }).isRequired,
    onResolve: PropTypes.func.isRequired,
    onDiscard: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ConfirmationCard);
